import { hyphenateSync } from "hyphen/fi";
import { format } from "date-fns";
import { fetchImages } from "../../utils/ImageHandler";
import waativacheckmark from '../../../assets/images/waativacheckmark.png';
import waativaLogo from '../../../assets/images/waativalogoteksilla.png';
import {
  PAGE_WIDTH,
  MARGIN_LEFT,
  MARGIN_RIGHT,
  MARGIN_Y,
  CONTENT_WIDTH,
  bottomMarginItemGlobal,
  lineHeightGlobal,
  INTEND_GLOBAL
} from "../../utils/pdfConstants";
import { cleanReportName } from "../../../utils/reportNameUtils";
import { deriveCoverStatus } from "../../utils/reportingStatesNormalization";

export async function renderCoverPageForm(block, content, context) {
  const coverData = block.data || {};
  const { rakennusData, report, effectiveMarginPt: marginPt = MARGIN_LEFT } = context;
  const effectiveContentWidth = PAGE_WIDTH - 2 * marginPt;
  const propertyId = coverData.propertyId || rakennusData?.id_kiinteisto;

  console.log("PDF coverData:", coverData);

  if (!propertyId) return;

  // Fetch images
  let { availableImages, imageUrls } = await fetchImages(propertyId, {
    category: "Kansikuva",
    subCategory: "Ei aliluokkaa",
    asBase64: true,
  });

  // Fallback if empty
  if (!availableImages?.length) {
    const fallback = await fetchImages(propertyId, {
      category: "Kansikuva",
      subCategory: "",
      asBase64: true,
    });
    availableImages = fallback.availableImages || [];
    imageUrls = fallback.imageUrls || {};
  }

  const splitTitle = (text) => {
    const words = text.split(" ");

    if (words.length <= 3) return text;

    const firstLine = words.slice(0, 3).join(" ");
    const secondLine = words.slice(3).join(" ");

    return `${firstLine}\n${secondLine}`;
  };

  const excludedIds = new Set(coverData.excludedImages ?? []);
  const coverImg = availableImages.find(img => !excludedIds.has(img.id)) ?? availableImages[0];
  const coverImageBase64 = coverImg ? imageUrls[coverImg.id] : null;

  if (!coverImageBase64) console.warn("No cover image available for PDF");

  // Center-crop a base64 image to the given aspect ratio using a canvas
  const centerCropToAspect = (base64, targetW, targetH) =>
    new Promise(resolve => {
      const img = new Image();
      img.onload = () => {
        const targetAspect = targetW / targetH;
        const srcAspect = img.naturalWidth / img.naturalHeight;
        let sx, sy, sw, sh;
        if (srcAspect > targetAspect) {
          sh = img.naturalHeight; sw = sh * targetAspect;
          sx = (img.naturalWidth - sw) / 2; sy = 0;
        } else {
          sw = img.naturalWidth; sh = sw / targetAspect;
          sx = 0; sy = (img.naturalHeight - sh) / 2;
        }
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(sw); canvas.height = Math.round(sh);
        canvas.getContext('2d').drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', 0.92));
      };
      img.onerror = () => resolve(base64);
      img.src = base64;
    });

  // Convert logos/checkmarks to base64
  const convertToBase64 = async (src) => {
    try {
      const res = await fetch(src);
      if (!res.ok) throw new Error("Fetch failed");

      const blob = await res.blob();

      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (err) {
      console.error("Failed to convert image to base64:", err);
      return null;
    }
  };

  const getTitleFontSize = (text) => {
    const length = text.length;

    if (length > 60) return 19;
    if (length > 50) return 22;
    if (length > 40) return 20;
    if (length > 35) return 26;
    return 30;
  };

  const logoBase64 = await convertToBase64(waativaLogo);
  const checkmarkBase64 = await convertToBase64(waativacheckmark);

  const IMAGE_HEIGHT = 380;
  const lineHeight = coverImageBase64 ? IMAGE_HEIGHT : 200;

  // 1️⃣ Title — same fallback the Kansilehti field itself uses
  // (CoverPageForm.jsx), so the PDF never shows a dev/version annotation
  // like "(V2.1)" that the field already hides by default. This used to be
  // its own separate inline regex here that didn't strip "(...)" tags —
  // the field and the PDF disagreed on the title until the user actually
  // typed something into "Raportin otsikko" (which finally set a real
  // coverData.title, bypassing the mismatch).
  const titleText = coverData.title || cleanReportName(report.name) || "Raportti";
  const STYLED_HEADER_TYPES = new Set(['julkisivujen_kuntotutkimus', 'julkisivujen_kuntotutkimus_v2', 'ikkunoiden_ja_ulko-ovien_kuntoarvio']);
  content.push(STYLED_HEADER_TYPES.has(report?.type)
    ? { text: titleText, fontSize: 36, alignment: "center", characterSpacing: 10, margin: [0, 0, 0, 0], noHyphenate: true }
    : { text: titleText, fontSize: 36, alignment: "left", margin: [0, 0, 0, 0] }
  );

  // 2️⃣ Left stack: cover image + property info
  const leftStack = [];
  const MARGIN_TOP_COLUMNITEMS = lineHeight / 3;
  // Separator column has real width so pdfmake creates physical spacing (width:0 + margin doesn't work)
  const separatorWidth = bottomMarginItemGlobal * 2; // 20pt total; 10pt gap on each side of line
  const leftStackWidth = effectiveContentWidth * 0.75;
  const rightStackWidth = effectiveContentWidth - leftStackWidth;
  // Adjust column widths so the line falls exactly at 3/4 of content width:
  //   adjustedLeftStackWidth + separatorWidth/2 = effectiveContentWidth * 0.75
  const adjustedLeftStackWidth = leftStackWidth - separatorWidth / 2;
  const adjustedRightStackWidth = rightStackWidth - separatorWidth / 2;
  const IMAGE_WIDTH = adjustedLeftStackWidth;

  let isPortrait = false;
  let renderedImageHeight = IMAGE_HEIGHT;
  let coverImageFinal = coverImageBase64;
  if (coverImageBase64) {
    const imgDims = await new Promise(resolve => {
      const img = new Image();
      img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
      img.onerror = () => resolve({ w: 1, h: 1 });
      img.src = coverImageBase64;
    });
    isPortrait = imgDims.h > imgDims.w;
    if (isPortrait) {
      coverImageFinal = await centerCropToAspect(coverImageBase64, IMAGE_WIDTH, IMAGE_HEIGHT);
      renderedImageHeight = IMAGE_HEIGHT;
    } else {
      renderedImageHeight = IMAGE_WIDTH * (imgDims.h / imgDims.w);
    }
    leftStack.push({
      image: coverImageFinal,
      width: IMAGE_WIDTH,
      alignment: "left",
      margin: [0, isPortrait ? MARGIN_TOP_COLUMNITEMS * 0.55 : MARGIN_TOP_COLUMNITEMS, 0, bottomMarginItemGlobal],
    });
  }

  // Property name
  const propertyName = (coverData.propertyName || rakennusData?.kohteen_nimi || "")
    .toUpperCase();

  const propertyNameSplit = splitTitle(propertyName);

  // Address
  const firstRakennus = rakennusData?.rakennukset_fulls?.[0];

  const osoiteFallback = firstRakennus
    ? `${firstRakennus.osoite || ""}, ${firstRakennus.postinumero || ""} ${firstRakennus.toimipaikka || ""}`.trim()
    : "";

  const osoite =
    coverData.osoiteToimipaikka?.trim()
      ? coverData.osoiteToimipaikka
      : osoiteFallback;

  leftStack.push(
    {
      text: propertyNameSplit,
      fontSize: getTitleFontSize(propertyName),
      alignment: "right",
      noWrap: false,
      margin: [0, 0, 0, bottomMarginItemGlobal],
    },
    {
      text: osoite,
      fontSize: 11,
      alignment: "right",
      margin: [0, 0, 0, 0],
    }
  );

  // Tila (luonnos/lopullinen) + versionumero — small label under the
  // address, same right-aligned idiom. Luonnos is flagged in amber so a
  // draft is never mistaken for the final document; lopullinen stays
  // muted/neutral since it's the expected end state.
  // Mirrors report.state unless the block's own manual override (coverData.status)
  // was stamped for this exact report.state and hasn't been invalidated by a
  // real status change since — same rule as CoverPageForm.jsx's own sync effect,
  // so the PDF is correct even if the cover page was never reopened after the
  // report's real state last changed.
  const effectiveStatus = coverData.statusReportState === report?.state
    ? coverData.status
    : deriveCoverStatus(report?.state);
  const statusLabel = effectiveStatus === "lopullinen" ? "LOPULLINEN" : "LUONNOS";
  const statusColor = effectiveStatus === "lopullinen" ? "#8e8e93" : "#b45309";
  const versionText = coverData.versionNumber?.trim() ? `Versio ${coverData.versionNumber.trim()}` : "";
  leftStack.push({
    text: [
      { text: statusLabel, bold: true, color: statusColor },
      versionText ? { text: `  ·  ${versionText}`, color: "#8e8e93" } : {},
    ],
    fontSize: 9,
    alignment: "right",
    margin: [0, bottomMarginItemGlobal / 2, 0, 0],
  });

  // 3️⃣ Vertical line — real column width creates physical spacing; line drawn at center.
  const middleColumn = {
    canvas: [
      {
        type: "line",
        x1: separatorWidth / 2,
        y1: 0,
        x2: separatorWidth / 2,
        y2: IMAGE_HEIGHT * 1.5,
        lineWidth: 1,
        color: "black"
      }
    ],
    width: separatorWidth,
  };

  // 4️⃣ Right stack: company + logos
  // Font size scales down so INSINÖÖRITOIMISTO (18 chars) always fits (≈0.60 × fontSize per char for Lato Bold)
  const insinooriFontSize = Math.max(7, Math.min(12, Math.floor(adjustedRightStackWidth / (18 * 0.60))));
  const LOGO_WIDTH = Math.min(150, adjustedRightStackWidth);

  const measureRenderedHeight = (base64, displayWidth) =>
    new Promise(resolve => {
      const img = new Image();
      img.onload = () => resolve(displayWidth * img.naturalHeight / img.naturalWidth);
      img.onerror = () => resolve(displayWidth);
      img.src = base64;
    });

  const logoBottomMargin = IMAGE_HEIGHT / 8.5;
  const renderedLogoHeight = logoBase64 ? await measureRenderedHeight(logoBase64, LOGO_WIDTH) : 0;
  const renderedCheckmarkHeight = checkmarkBase64 ? await measureRenderedHeight(checkmarkBase64, LOGO_WIDTH) : 0;
  const approxTextHeight = insinooriFontSize * lineHeightGlobal * 2;

  const checkmarkTopMargin = Math.max(
    bottomMarginItemGlobal,
    renderedImageHeight - approxTextHeight - renderedLogoHeight - logoBottomMargin - renderedCheckmarkHeight
  );

  const rightStack = {
    stack: [
      {
        text: [
          { text: "INSINÖÖRITOIMISTO", bold: true, fontSize: insinooriFontSize, noWrap: true },
          "\n",
          { text: "WAATIVA", bold: true, fontSize: insinooriFontSize }
        ],
        alignment: "center",
        lineHeight: lineHeightGlobal,
        margin: [0, isPortrait ? MARGIN_TOP_COLUMNITEMS * 0.55 : MARGIN_TOP_COLUMNITEMS, 0, 0]
      },

      ...(logoBase64
        ? [{
            image: logoBase64,
            width: LOGO_WIDTH,
            alignment: "center",
            margin: [0, 0, 0, logoBottomMargin]
          }]
        : []),

      ...(checkmarkBase64
        ? [{
            image: checkmarkBase64,
            width: LOGO_WIDTH,
            alignment: "center",
            margin: [0, checkmarkTopMargin, 0, 0]
          }]
        : [])
    ],
    width: adjustedRightStackWidth,
  };

  // 5️⃣ Combine columns
  content.push({
    columns: [
      { stack: leftStack, width: adjustedLeftStackWidth },
      middleColumn,
      rightStack
    ],
    columnGap: 0,
    margin: [0, 0, 0, 0]
  });

  // 6️⃣ Page break
  content.push({
    text: "",
    margin: [0, 10, 0, 0]
  });
}